import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';

import {
  useGetGestionTicket,
  useUpdateGestionTicket,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import { useUpdateVisita } from '@/shared/actions/erp/ordenServicio/orden-servicio-vista.actions';
import {
  useFetchTicketPrioridades,
  useFetchTicketAreas,
  useFetchTicketCategorias,
  useFetchTicketSubcategorias,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import { ROUTER_PATHS } from '@/shared/constants/routes/router-paths.constants';

import HeaderTicket from '../components/HeaderTicket';
import CardCliente from '../components/CardCliente';
import TrasladoForm from '../../nuevo-ticket/shared/components/OrdenServicioTraslado/TrasladoForm';
import VisitaForm from '../../nuevo-ticket/shared/components/OrdenServicioVisita/VisitaForm';
import { Contrato, Sector } from '@/shared/interfaces';
import { useLocalidad } from '@/shared/hooks/useLocalidad';
import { obtenerUbicacion, useMapComponent } from '@/shared';
import { useForm } from 'react-hook-form';
import { getSector, postPoligono, useFetchContratos, } from '@/shared/actions/erp';


export default function EditarTicketPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();

  const { data: ticketDataResp, isLoading: isLoadingTicket } = useGetGestionTicket(ticketId!);
  const { data: dataContrato } =
    useFetchContratos({
      params: {
        identificacion: ticketDataResp?.data.ticket?.cliente_data?.identificacion,
      },
    });


  const ticketData = useMemo(() => {
    return ticketDataResp?.data.ticket;
  }, [isLoadingTicket]);

  const ordenServicioData = useMemo(() => {
    return ticketDataResp?.data.orden_servicio;
  }, [isLoadingTicket]);

  const visitaSectorData = useMemo(() => {
    const visita = ordenServicioData?.visita;
    if (!visita) return null;
    return {
      pais_data: { nombre: visita.pais },
      provincia_data: { nombre: visita.provincia },
      ciudad_data: { nombre: visita.ciudad },
      zona_data: { nombre: visita.zona },
      nombre: visita.parroquia,
    } as any;
  }, [ordenServicioData]);

  const visitaContratoData = useMemo(() => {
    // Use fetched contrato data if available (complete), otherwise fallback to ticket data
    const clienteData = dataContrato?.data?.data?.[0]?.cliente_data || ticketData?.cliente_data;
    const contratoInfo = dataContrato?.data?.data?.[0] || ticketData?.contrato_data;

    if (!clienteData) return null;

    return {
      ...clienteData,
      cliente_data: clienteData,
      plan_data: contratoInfo?.plan_data,
      direccion: ordenServicioData?.visita?.direccion || clienteData?.direccion || '',
    } as any;
  }, [ticketData, ordenServicioData, dataContrato]);

  // Catalogs
  const { data: dataPrioridades } = useFetchTicketPrioridades({ page_size: 50 });
  const { data: dataAreas } = useFetchTicketAreas({ page_size: 50 });

  const prioridades = (dataPrioridades?.data as any)?.data || dataPrioridades?.data || [];
  const areas = (dataAreas?.data as any)?.data || dataAreas?.data || [];

  // Form state
  const [prioridad, setPrioridad] = useState<string>('');
  const [areaDestino, setAreaDestino] = useState<string>('');
  const [categoria, setCategoria] = useState<string>('');
  const [subcategoria, setSubcategoria] = useState<string>('');
  const [descripcion, setDescripcion] = useState('');
  const [requiereVisita, setRequiereVisita] = useState(false);
  const [esTicketRapido, setEsTicketRapido] = useState(false);
  const [contactoNombre, setContactoNombre] = useState('');
  const [contactoTelefono, setContactoTelefono] = useState('');
  const [contactoTipo, setContactoTipo] = useState('');

  // Dependent catalogs
  const ticket = ticketData;

  const { data: dataCategorias } = useFetchTicketCategorias(ticket?.tipo || undefined);
  const { data: dataSubcategorias } = useFetchTicketSubcategorias(categoria || undefined);

  const categorias = (dataCategorias?.data as any)?.data || dataCategorias?.data || [];
  const subcategorias = (dataSubcategorias?.data as any)?.data || dataSubcategorias?.data || [];

  // Populate form when ticket data loads
  useEffect(() => {
    if (ticket) {
      setPrioridad(ticket.prioridad || '');
      setAreaDestino(ticket.area_destino || '');
      setCategoria(ticket.categoria || '');
      setSubcategoria(ticket.subcategoria || '');
      setDescripcion(ticket.descripcion || '');
      setRequiereVisita(ticket.requiere_visita || false);
      setEsTicketRapido(ticket.es_ticket_rapido || false);
      setContactoNombre(ticket.contacto_nombre || '');
      setContactoTelefono(ticket.contacto_telefono || '');
      setContactoTipo(ticket.contacto_tipo || '');
    }
  }, [ticket]);

  const returnUrl = `${ROUTER_PATHS.ticketSoporteCliente.detalle_ticket_nav}/${ticketId}`;

  const updateMutation = useUpdateGestionTicket({
    navigate,
    returnUrl,
    enableRedirect: true,
    enableSuccessAlert: true,
    customSuccessMessage: 'Ticket actualizado correctamente',
  });

  const { mutate: actualizarVisita } = useUpdateVisita({
    navigate,
    returnUrl,
    ticketId,
    //returnUrl: `${ROUTER_PATHS.ticketSoporteCliente.mis_tickets_nav}`,
    enableRedirect: true,
    enableSuccessAlert: true,
    customSuccessMessage: 'Visita actualizada',
  });


  // edit traslado

  // El value de modalMapa no se lee directamente; el modal se controla
  // pasando el setter al hijo (onOpenMapa). Mantenemos el setter accesible.
  const [, setModalMapa] = useState(false);
  //const [serviceType, setServiceType] = useState<ServiceTypeOrdenServicio>(null);
  const [existeCobertura, setExisteCobertura] = useState(false);
  const [contrato, setContrato] = useState<Contrato | null>(null)
  console.log('contrato', contrato)
  // form: el tipo EditarTicketPageForm no esta exportado en este modulo.
  // Usamos any para no romper la firma de useForm; el comportamiento del
  // form (validacion, submit) no cambia.
  const form = useForm<any>()

  const { reset } = form;


  // recuperar valores de traslado
  useEffect(() => {
    if (!ticket) return
    const tipoOrden = ordenServicioData?.tipo_orden_servicio
    const traslado = ordenServicioData?.traslado
    const visita = ordenServicioData?.visita

    if (tipoOrden === 'TRASLADO' && traslado?.coordenada) {
      handleUbicacionDefecto(traslado.coordenada)
    }

    const currentValues: any = form.getValues() as any;
    reset({
      ...currentValues,
      coordenada: tipoOrden === 'TRASLADO' ? traslado?.coordenada : currentValues?.coordenada,
      direccion: tipoOrden === 'TRASLADO' ? traslado?.direccion : (visita?.direccion || currentValues?.direccion),
      pais: tipoOrden === 'VISITA' ? (visita?.pais || '') : currentValues?.pais,
      provincia: tipoOrden === 'VISITA' ? (visita?.provincia || '') : currentValues?.provincia,
      ciudad: tipoOrden === 'VISITA' ? (visita?.ciudad || '') : currentValues?.ciudad,
      parroquia: tipoOrden === 'VISITA' ? (visita?.parroquia || '') : currentValues?.parroquia,
      zona: tipoOrden === 'VISITA' ? (visita?.zona || '') : currentValues?.zona,
      observaciones: tipoOrden === 'VISITA' ? (visita?.observaciones || '') : currentValues?.observaciones,
      fecha_visita: tipoOrden === 'VISITA' ? (visita?.fecha_visita || '') : currentValues?.fecha_visita,
    } as any)

    setContrato(dataContrato?.data?.data[0] || null)

  }, [ticket?.id_ticket, ordenServicioData, dataContrato])

  const {
    setLatLng,
    napsByCity,
    isLoadingNapsByCity,
    setCurrentCoordenadas,
  } = useMapComponent({
    form,
    currentCity: "",
    coordenadasCliente: "",
    enabledFetchNaps: true,
    canUpdateLatLong: true,
    async onChangeMarker(value) {
      if (!value) return;
      handleVerificarCoordenada(value.lat.toString(), value.lng.toString());
    },
  });


  const {
    dataSector,
    isLoadingSector
  } = useLocalidad({
    form: form as any,
    cobertura: true,
  });

  // Obtener ubicacion cuando es editar

  async function handleUbicacionDefecto(coordenadas: string) {
    let s = coordenadas.split(",");
    setLatLng({ lat: +s?.at(0)!, lng: +s?.at(1)! });
    setCurrentCoordenadas(coordenadas!);
    //onChangeProvince(dataPreventa?.provincia_data?.codigo!);
    await handleVerificarCoordenada(s?.at(0)!, s?.at(1)!);

  }

  // manejar coordenada valida
  const handleCoordenadaValida = (
    value: string,
    valid: boolean | undefined
  ) => {
    if (!valid || !value) return;
    let coord = value.split(",");
    setLatLng({ lat: +coord[0], lng: +coord[1] });
    setCurrentCoordenadas(value);
    handleVerificarCoordenada(coord[0], coord[1]);
    /* reset({
      ...form.getValues(),
    }); */
  };

  // obtener ubicacion actual por GPS

  const handleApiUbicacioActual = async () => {
    try {
      const coordenadasApi = await obtenerUbicacion();
      const { latitude, longitude } = coordenadasApi;
      setLatLng({
        lat: latitude,
        lng: longitude,
      });
      setCurrentCoordenadas(`${latitude},${longitude}`);
      reset({
        ...form.getValues(),
        coordenadas: `${latitude},${longitude}`,
        latitud: latitude.toString(),
        longitud: longitude.toString(),
      });

      handleVerificarCoordenada(latitude.toString(), longitude.toString());
    } catch (error) {
      console.log(error, "error");
    } finally {
    }
  };

  // verificar si existe cobertura acorde a las coordendas ingresadas

  const handleVerificarCoordenada = useCallback(
    async (lat: string, lng: string) => {
      try {
        const resp = await postPoligono({
          latitud: lat,
          longitud: lng,
        });


        // TODO: valores obtenidos para el traslado, el microservicio tiene relacion con los id de los modelos
        let sector_cod = null
        let sector_id = null
        let parroquia_id = null

        if (ordenServicioData?.traslado.coordenada == `${lat},${lng}`) {
          const sectorRes = await getSector(ordenServicioData?.traslado.sector!)
          sector_cod = sectorRes?.data.codigo
          sector_id = sectorRes?.data.id_sector
          parroquia_id = sectorRes?.data.id_sector // TODO: revisar si es sector o parroquia
        }

        reset({
          ...form.getValues(),
          pais: resp.data.pais_data?.codigo,
          zona: resp.data.codigo,
          ciudad: resp.data.ciudad_data?.codigo,
          provincia: resp.data.provincia_data?.codigo,
          provincia_nombre: resp.data.provincia_data?.nombre,
          ciudad_nombre: resp.data.ciudad_data?.nombre,
          zona_nombre: resp.data.nombre,
          // id's
          pais_id: resp.data.pais_data?.id_pais,
          zona_id: resp.data.id_zona,
          ciudad_id: resp.data.ciudad_data?.id_ciudad,
          provincia_id: resp.data.provincia_data?.id_provincia,
          //sector: sector?.data.codigo, //TODO: revisar si es sector o parroquia
          sector: sector_cod,
          sector_id: sector_id,
          parroquia_id: parroquia_id
        });



        setExisteCobertura(true);
      } catch (error) {
        setExisteCobertura(false);
        reset({
          ...form.getValues(),
          pais: "",
          zona: "",
          ciudad: "",
          provincia: "",
          zona_nombre: "",
          provincia_nombre: "",
          ciudad_nombre: "",
        });
      }
    },
    [ordenServicioData]
  );

  // seleccionar sector
  const handleSelectSector = async (value: Sector) => {
    // TODO: Implement sector selection logic
    console.log("Selected sector:", value);

    reset({
      ...form.getValues(),
      parroquia: value.codigo,
      sector: value.codigo,
      parroquia_id: value.id_sector,
      sector_id: value.id_sector
    });
  }

  // save
  const handleSave = () => {
    if (!ticket) return;

    const value: any = form.getValues();

    if (ordenServicioData?.tipo_orden_servicio === 'VISITA') {
      const idVisita = ordenServicioData?.visita?.id_visita;
      if (!idVisita) return;

      const payload: { observaciones?: string; fecha_visita?: string } = {};

      if (value.observaciones?.trim()) {
        payload.observaciones = value.observaciones.trim();
      }

      if (value.fecha_visita) {
        payload.fecha_visita = value.fecha_visita.replace(' ', 'T');
      }

      actualizarVisita({
        idVisita,
        data: payload,
      });
      return;
    }

    // _traslado: payload preparado para una posible mutacion del traslado.
    // Hoy no se envia (el mutate de abajo solo actualiza el ticket); se
    // conserva para integracion futura con el microservicio orden-servicio.
    const _traslado = {
      id_traslado: ordenServicioData?.traslado?.id_traslado,
      pais: value.pais_id,
      provincia: value.provincia_id,
      ciudad: value.ciudad_id,
      zona: value.zona_id,
      sector: value.sector_id,
      parroquia: value.parroquia_id,
      coordenada: value.coordenadas,
      direccion: value.direccion,
    };
    void _traslado;


    updateMutation.mutate({
      id: ticketId!,
      data: {
        tipo: ticket.tipo,
        estado_ticket: ticket.estado_ticket,
        prioridad,
        area_origen: ticket.area_origen,
        area_destino: areaDestino,
        categoria: categoria || null,
        subcategoria: subcategoria || null,
        descripcion,
        requiere_visita: requiereVisita,
        es_ticket_rapido: esTicketRapido,
        contacto_nombre: contactoNombre,
        contacto_tipo: contactoTipo,
        contacto_telefono: contactoTelefono,
        cliente_id: ticket.cliente_id ?? (ticket.cliente as any),
        contrato_id: ticket.contrato_id ?? (ticket.contrato as any),
        cliente_identificacion: ticket.cliente_identificacion,
        cliente_razon_social: ticket.cliente_razon_social
      },
    });
  };

  if (isLoadingTicket) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Ticket no encontrado
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2, textTransform: 'none' }}
        >
          Volver
        </Button>
      </Paper>
    );
  }

  const isClosed = ticket.estado_ticket_data?.codigo === 'cerrado' || ticket.estado_ticket_data?.codigo === 'cancelado';
  //useLoaders(isLoadingContrato);
  return (
    <>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Volver">
              <IconButton onClick={() => navigate(-1)} size="small">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon color="warning" />
              EDITAR TICKET
            </Typography>
            <Chip label={ticket.codigo_ticket} color="primary" size="small" sx={{ ml: 1 }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(returnUrl)}
              sx={{ textTransform: 'none' }}
            >
              Cancelar
            </Button>
            {!isClosed && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={updateMutation.isPending}
                sx={{ textTransform: 'none', fontWeight: 500 }}
              >
                Guardar Cambios
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />
        <HeaderTicket ticket={ticket} />
      </Paper>

      {/* Content */}
      <Grid container spacing={2}>
        {/* Left Column - Edit Form */}
        <Grid item xs={12} md={8}>
          {isClosed && (
            <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#fff3e0' }}>
              <Typography variant="body2" color="warning.main" fontWeight={500}>
                Este ticket esta cerrado/cancelado y no puede ser editado.
              </Typography>
            </Paper>
          )}

          {/* Prioridad & Area */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
              Clasificacion
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={prioridades}
                  getOptionLabel={(opt: any) => opt.nombre}
                  value={prioridades.find((p: any) => p.id_ticket_prioridad === prioridad) || null}
                  onChange={(_, val) => setPrioridad(val?.id_ticket_prioridad || '')}
                  disabled={isClosed}
                  renderInput={(params) => <TextField {...params} label="Prioridad" size="small" />}
                  renderOption={(props, option: any) => (
                    <li {...props} key={option.id_ticket_prioridad}>
                      <Chip
                        label={option.nombre}
                        size="small"
                        sx={{ bgcolor: option.color, color: '#fff', mr: 1 }}
                      />
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={areas}
                  getOptionLabel={(opt: any) => opt.nombre}
                  value={areas.find((a: any) => a.id_area === areaDestino) || null}
                  onChange={(_, val) => setAreaDestino(val?.id_area || '')}
                  disabled={isClosed}
                  renderInput={(params) => <TextField {...params} label="Area Destino" size="small" />}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Categoria & Subcategoria */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
              Categorizacion
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={categorias}
                  getOptionLabel={(opt: any) => opt.nombre}
                  value={categorias.find((c: any) => c.id_ticket_categoria === categoria) || null}
                  onChange={(_, val) => {
                    setCategoria(val?.id_ticket_categoria || '');
                    setSubcategoria('');
                  }}
                  disabled={isClosed}
                  renderInput={(params) => <TextField {...params} label="Categoria" size="small" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={subcategorias}
                  getOptionLabel={(opt: any) => opt.nombre}
                  value={subcategorias.find((s: any) => s.id_ticket_subcategoria === subcategoria) || null}
                  onChange={(_, val) => setSubcategoria(val?.id_ticket_subcategoria || '')}
                  disabled={isClosed || !categoria}
                  renderInput={(params) => <TextField {...params} label="Subcategoria" size="small" />}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Descripcion */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
              Descripcion
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TextField
              fullWidth
              multiline
              rows={4}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              disabled={isClosed}
              placeholder="Describa detalladamente la incidencia..."
            />
          </Paper>

          {/* Contacto */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
              Informacion de Contacto
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre de Contacto"
                  value={contactoNombre}
                  onChange={(e) => setContactoNombre(e.target.value)}
                  disabled={isClosed}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Telefono"
                  value={contactoTelefono}
                  onChange={(e) => setContactoTelefono(e.target.value)}
                  disabled={isClosed}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tipo Contacto"
                  value={contactoTipo}
                  onChange={(e) => setContactoTipo(e.target.value)}
                  disabled={isClosed}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Orden de servicio */}

          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
              Orden de Servicio
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {ordenServicioData?.tipo_orden_servicio === 'TRASLADO' && (
              <TrasladoForm
                contrato={contrato}
                form={form}
                existeCobertura={existeCobertura}
                dataSector={dataSector}
                isLoadingSector={isLoadingSector}
                napsByCity={napsByCity}
                isLoadingNapsByCity={isLoadingNapsByCity}
                onCoordenadaValida={handleCoordenadaValida}
                onSelectSector={handleSelectSector}
                onOpenMapa={() => setModalMapa(true)}
                onObtenerUbicacionActual={handleApiUbicacioActual}
              />
            )}

            {ordenServicioData?.tipo_orden_servicio === 'VISITA' && (
              <VisitaForm
                contrato={visitaContratoData}
                sector={visitaSectorData}
                form={form}
              />
            )}

            {!ordenServicioData || (ordenServicioData?.tipo_orden_servicio !== 'TRASLADO' && ordenServicioData?.tipo_orden_servicio !== 'VISITA') ? (
              <Alert severity="info">
                No existe orden de servicio para editar en este ticket.
              </Alert>
            ) : null}
          </Paper>

          {/* Opciones */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
              Opciones
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={requiereVisita}
                      onChange={(e) => setRequiereVisita(e.target.checked)}
                      disabled={isClosed}
                      color="warning"
                    />
                  }
                  label="Requiere Visita Tecnica"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={esTicketRapido}
                      onChange={(e) => setEsTicketRapido(e.target.checked)}
                      disabled={isClosed}
                      color="info"
                    />
                  }
                  label="Ticket Rapido"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Column - Client Info (read-only) */}
        <Grid item xs={12} md={4}>
          <CardCliente ticket={ticket} />

          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
              Informacion del Ticket (Solo Lectura)
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Estado</Typography>
                <Box>
                  <Chip
                    label={ticket.estado_ticket_data?.nombre || '-'}
                    size="small"
                    sx={{
                      bgcolor: ticket.estado_ticket_data?.color || '#757575',
                      color: '#fff',
                    }}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Tipo</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {ticket.tipo_data?.nombre || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Canal</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {ticket.canal_data?.nombre || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Creado por</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {ticket.usuario_creador_nombre}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Asignado a</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {ticket.usuario_asignado_nombre || 'Sin asignar'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
